import React, { useState, useEffect } from 'react';
import { apiService } from '../apiService';
import './FlowServiceAnalyzer.css';
import { severityHierarchy } from './FlowServiceAnalyzer';
import SeveritySelector from './SeveritySelector';

interface TagsServiceAnalyzerProps {
  callId: string;
  domainId: string;
  projectId: string;
  callStartTimestamp?: string;
  callEndTimestamp?: string;
  onClose: () => void;
}

interface LogEvent {
  timestamp: string;
  eventType: string;
  callId: string;
  thread: string;
  pod: string;
  duration: number;
  methodName: string;
  message: string;
  severity: string;
  details: any;
}

interface AnalysisStats {
  totalEvents: number;
  avgDuration: number;
  slowestEvent: number;
  fastestEvent: number;
  uniqueThreads: number;
  eventTypes: Record<string, number>;
}

interface CustomSummaryDetail {
  agentId: string;
  callId: string;
  collectionId: string;
  mediaType: string;
  promptId: string;
  reason: string;
  success?: boolean;
}

interface DefaultSummaryDetail {
  agentId: string;
  callId: string;
  collectionId: string;
  mediaType: string;
  promptId: string;
  reason: string;
  success?: boolean;
}

const TagsServiceAnalyzer: React.FC<TagsServiceAnalyzerProps> = ({ 
  callId, 
  domainId, 
  projectId,
  callStartTimestamp,
  callEndTimestamp,
  onClose 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [stats, setStats] = useState<AnalysisStats | null>(null);
  const [customSummaryDetails, setCustomSummaryDetails] = useState<CustomSummaryDetail[]>([]);
  const [defaultSummaryDetails, setDefaultSummaryDetails] = useState<DefaultSummaryDetail[]>([]);
  const [expandedEvent, setExpandedEvent] = useState<number | null>(null);
  const [filterEventType, setFilterEventType] = useState('');
  const [filterThread, setFilterThread] = useState('');
  const [minDuration, setMinDuration] = useState<number>(0);
  const [severity, setSeverity] = useState('info');

  const analyzeTagsServiceLogs = React.useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiService.analyzeTagsServiceLogs(
        callId, 
        domainId, 
        projectId,
        callStartTimestamp,
        callEndTimestamp
      );
      setEvents(response.events);
      setStats(response.stats);
      setCustomSummaryDetails(response.customSummaryDetails || []);
      setDefaultSummaryDetails(response.defaultSummaryDetails || []);
    } catch (err: any) {
      setError(err.message || 'Failed to analyze Tags Service logs');
    } finally {
      setLoading(false);
    }
  }, [callId, domainId, projectId, callStartTimestamp, callEndTimestamp]);

  useEffect(() => {
    if (callId && domainId && projectId) {
      analyzeTagsServiceLogs();
    }
  }, [callId, domainId, projectId, analyzeTagsServiceLogs]);

  const filteredEvents = events.filter(event => {
    if (filterEventType && !event.eventType.toLowerCase().includes(filterEventType.toLowerCase())) {
      return false;
    }
    if (filterThread && !event.thread.toLowerCase().includes(filterThread.toLowerCase())) {
      return false;
    }
    if (minDuration && event.duration < minDuration) {
      return false;
    }
    if (severity && event.severity) {
      const eventSeverityLevel = severityHierarchy[event.severity.toLowerCase()];
      const filterSeverityLevel = severityHierarchy[severity];
      if (eventSeverityLevel < filterSeverityLevel) {
        return false;
      }
    }
    return true;
  });

  const formatDuration = (duration: number) => {
    if (duration === 0) return { display: '-', className: '' };
    const ms = Math.round(duration);
    const className = ms < 100 ? 'fast' : ms < 500 ? 'medium' : 'slow';
    return { display: `${ms}ms`, className, ms };
  };

  const getEventTypeClass = (eventType: string) => {
    const type = eventType.toLowerCase().replace(/_/g, '-');
    return `event-type ${type}`;
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="flow-analyzer-overlay" onClick={handleOverlayClick}>
        <div className="flow-analyzer-modal" onClick={(e) => e.stopPropagation()}>
          <div className="flow-analyzer-header">
            <h3>Tags Service Log Analyzer</h3>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Analyzing Tags Service logs for call {callId}...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flow-analyzer-overlay" onClick={handleOverlayClick}>
        <div className="flow-analyzer-modal" onClick={(e) => e.stopPropagation()}>
          <div className="flow-analyzer-header">
            <h3>Tags Service Log Analyzer</h3>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="error-state">
            <p>Error: {error}</p>
            <button onClick={analyzeTagsServiceLogs} className="retry-btn">
              Retry Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flow-analyzer-overlay" onClick={handleOverlayClick}>
      <div className="flow-analyzer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flow-analyzer-header">
          <h3>Tags Service Log Analyzer</h3>
          <div className="header-info">
            <span>Call ID: {callId}</span>
            <span>Domain: {domainId}</span>
            <span>Project: {projectId}</span>
          </div>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {stats && (
          <div className="stats-section">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">{stats.totalEvents}</div>
                <div className="stat-label">Total Events</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{Math.round(stats.avgDuration)}ms</div>
                <div className="stat-label">Avg Duration</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{Math.round(stats.slowestEvent)}ms</div>
                <div className="stat-label">Slowest Event</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{Math.round(stats.fastestEvent)}ms</div>
                <div className="stat-label">Fastest Event</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{stats.uniqueThreads}</div>
                <div className="stat-label">Unique Threads</div>
              </div>
            </div>
          </div>
        )}

        {customSummaryDetails && customSummaryDetails.length > 0 && (
          <div className="stats-section">
            <h4>Failure Reasons (Custom Summary) - {customSummaryDetails.filter(d => d.success === false || d.success === undefined).length} errors</h4>
            <div className="custom-summary-container">
              {customSummaryDetails.filter((detail) => detail.success === false || detail.success === undefined).map((detail, index) => (
                <div key={index} className="custom-summary-card">
                  <div className="custom-summary-header">
                    <h5 className="summary-title">{detail.reason || 'Custom Summary Detail'}</h5>
                  </div>
                  <div className="custom-summary-grid">
                    <div className="summary-field full-width">
                      <label>Collection ID</label>
                      <div className="value code">{detail.collectionId}</div>
                    </div>
                    <div className="summary-field full-width">
                      <label>Prompt ID</label>
                      <div className="value code">{detail.promptId}</div>
                    </div>
                    <div className="summary-field">
                      <label>Media Type</label>
                      <div className="value">{detail.mediaType === 'null' ? <span className="null-value">null</span> : detail.mediaType}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {defaultSummaryDetails && defaultSummaryDetails.length > 0 && (
          <div className="stats-section">
            <h4>Failure Reasons (Default Summary) - {defaultSummaryDetails.filter(d => d.success === false || d.success === undefined).length} errors</h4>
            <div className="custom-summary-container">
              {defaultSummaryDetails.filter((detail) => detail.success === false || detail.success === undefined).map((detail, index) => (
                <div key={index} className="custom-summary-card">
                  <div className="custom-summary-header">
                    <h5 className="summary-title">{detail.reason || 'Default Summary Error'}</h5>
                  </div>
                  <div className="custom-summary-grid">
                    <div className="summary-field">
                      <label>Collection ID</label>
                      <div className="value code">{detail.collectionId}</div>
                    </div>
                    <div className="summary-field">
                      <label>Prompt ID</label>
                      <div className="value code">{detail.promptId}</div>
                    </div>
                    <div className="summary-field">
                      <label>Media Type</label>
                      <div className="value">{detail.mediaType === 'null' ? <span className="null-value">null</span> : detail.mediaType}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="filters-section">
          <div className="filter-group">
            <label>Event Type:</label>
            <input
              type="text"
              value={filterEventType}
              onChange={(e) => setFilterEventType(e.target.value)}
              placeholder="Filter by event type"
            />
          </div>
          <div className="filter-group">
            <label>Thread:</label>
            <input
              type="text"
              value={filterThread}
              onChange={(e) => setFilterThread(e.target.value)}
              placeholder="e.g., Gax-46"
            />
          </div>
          <div className="filter-group">
            <label>Min Duration (ms):</label>
            <input
              type="number"
              value={minDuration}
              onChange={(e) => setMinDuration(Number(e.target.value))}
              placeholder="0"
            />
          </div>
          <SeveritySelector severity={severity} setSeverity={setSeverity} />
        </div>

        <div className="events-section">
          <h4>Log Events ({filteredEvents.length})</h4>
          <div className="events-table-container">
            <table className="events-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Event Type</th>
                  <th>Thread</th>
                  <th>Pod</th>
                <th>Duration</th>
                <th>Method</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
                {filteredEvents.map((event, index) => {
                  const duration = formatDuration(event.duration);
                  return (
                    <React.Fragment key={index}>
                      <tr 
                        className={`event-row ${expandedEvent === index ? 'expanded' : ''}`}
                        onClick={() => setExpandedEvent(expandedEvent === index ? null : index)}
                      >
                        <td>{new Date(event.timestamp).toLocaleTimeString()}</td>
                        <td>
                          <span className={getEventTypeClass(event.eventType)}>
                            {event.eventType}
                          </span>
                        </td>
                        <td className="thread-info">{event.thread}</td>
                        <td>{event.pod.split('-').slice(-1)[0]}</td>
                        <td>
                          <span className={`duration ${duration.className}`}>
                            {duration.display}
                          </span>
                        </td>
                        <td className="method-name">{event.methodName}</td>
                        <td>
                          <button className="expand-btn">
                            {expandedEvent === index ? '▼' : '▶'}
                          </button>
                        </td>
                      </tr>
                      {expandedEvent === index && (
                        <tr className="event-details-row">
                          <td colSpan={7}>
                            <div className="event-details">
                              <div className="detail-section">
                                <h5>Message:</h5>
                                <pre>{event.message}</pre>
                              </div>
                              {event.details && Object.keys(event.details).length > 0 && (
                                <div className="detail-section">
                                  <h5>Event Details:</h5>
                                  <pre>{JSON.stringify(event.details, null, 2)}</pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagsServiceAnalyzer;
